import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getPolyglot } from '../../../../i18n';
import { DialogModal } from '../../../../theme/components';
import { useFormik } from 'formik';
import { ErrorText } from '../../../../theme/components/Forms';
import { useAppSelector } from '../../../../redux/store.model';
import { getMachinesTypeSelector } from '../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import * as Yup from 'yup';
import { useShowErrorMessage } from '../../../../handlingErrors';
import { useShowMessage } from '../../../../util/hooks';
import { useCreateMerchandiser } from '../../redux/composableDevice/hook/useCreateMerchandiser';
import StoerkIdAPI from '../../../../redux/stoerkID/StoerkIdAPI';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import AddressFormFields from '../../theme/components/AddressFormFields.tsx/AddressFormFields';
import { getGroupByIdWithFallbackSelector } from '../../../../redux/groups/selectors';
import { useGroups } from '../../../../redux/groups/hooks/useGroups';

interface AddMerchandiserModalProps {
  groupId: string;
  open: boolean;
  onClose: () => void;
}
const validationSchema = (articleNumbers: string[], skipValidation: boolean) =>
  Yup.object().shape({
    merchandiserName: Yup.string().required(
      'freshwatch.merchandiser.add.merchandiser_name_required'
    ),
    merchandiserType: Yup.string().required(
      'freshwatch.merchandiser.add.merchandiser_type_required'
    ),
    commanderSerialNumber: Yup.string()
      .required('freshwatch.merchandiser.add.freshwatch_code_required')
      .test(
        'is-serial-number-found',
        'freshwatch.merchandiser.add.freshwatch_code_not_found',
        function (value) {
          if (skipValidation) return true;
          const serialArticleNumber = value.split('-')[0];
          const articleNumberFound = articleNumbers?.find(
            (articleNumber: string) => articleNumber === serialArticleNumber
          );
          if (!articleNumberFound) {
            return false;
          }

          return true;
        }
      )
      .test(
        'is-valid-serial-number',
        'freshwatch.merchandiser.add.freshwatch_code_invalid_syntax',
        function (value) {
          return /^\d{6}\.\d{3}-\d{6}-\d{5}$/.test(value || '');
        }
      ),
  });

const AddMerchandiserModal = ({
  groupId,
  open,
  onClose,
}: AddMerchandiserModalProps) => {
  const polyglot = getPolyglot();
  const machineTypes = useAppSelector(getMachinesTypeSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const workspace = useAppSelector(getMyWorkspaceSelector);
  const workspaceId = workspace?.id ?? '';
  const [createMerchandiser] = useCreateMerchandiser(groupId, workspaceId);
  const {
    data: articleNumbers,
    isLoading: isLoadingArticleNumbers,
    error: articleNumbersError,
  } = StoerkIdAPI.useGetArticleNumbersFromWorkspaceQuery(
    {
      workspaceId,
    },
    { skip: !open }
  );

  const group = useAppSelector((state) =>
    getGroupByIdWithFallbackSelector(state, { groupId, workspaceId })
  );
  const showError = useShowErrorMessage();
  const showMessage = useShowMessage();

  if (articleNumbersError) {
    showError(
      polyglot.t(
        'freshwatch.merchandiser.add.could_not_load_merchandiser_types'
      )
    );
  }

  useEffect(() => {
    formik.resetForm();
  }, [open]);
  const initialValues = {
    merchandiserType: '',
    commanderSerialNumber: '',
    merchandiserName: '',
    street: group?.attributes?.street || '',
    zipCode: group?.attributes?.zipCode || '',
    city: group?.attributes?.city || '',
    country: group?.attributes?.country || '',
  };
  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema(
      articleNumbers ?? [],
      workspace?.name === 'internal-dev-workspace'
    ),
    onSubmit: async (values) => {
      formik.setTouched({
        merchandiserName: true,
        merchandiserType: true,
        commanderSerialNumber: true,
      });
      if (!formik.isValid) return;
      try {
        setIsSubmitting(true);
        const t = await createMerchandiser({
          merchandiserName: values.merchandiserName,
          merchandiserType: values.merchandiserType,
          commanderSerialNumber: values.commanderSerialNumber,
          location: {
            street: values.street,
            zipCode: values.zipCode,
            city: values.city,
            country: values.country,
          },
        });
        showMessage(polyglot.t('freshwatch.merchandiser.add.success'));
      } catch (error) {
        showError(error);
      } finally {
        setIsSubmitting(false);
        onClose();
      }
    },
  });

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      buttonActions={<></>}
      PaperProps={{ elevation: 0, boxShadow: 'none' }}
    >
      {!isLoadingArticleNumbers ? (
        <Stack gap={2}>
          <Typography variant="h3" gutterBottom>
            {polyglot.t('freshwatch.add_merchandiser')}
          </Typography>
          <Stack display={'flex'} gap="1rem" flexDirection="row">
            <form style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
              <Stack
                spacing={2}
                direction="column"
                sx={{ paddingBottom: '2rem' }}
              >
                <FormControl
                  fullWidth
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '0.5rem',
                  }}
                >
                  <FormLabel
                    htmlFor="merchandiserName"
                    required
                    sx={{ color: 'text.primary' }}
                  >
                    {polyglot.t(
                      'freshwatch.merchandiser.add.merchandiser_name'
                    )}
                  </FormLabel>

                  <TextField
                    fullWidth
                    id="merchandiserName"
                    name="merchandiserName"
                    placeholder={polyglot.t(
                      'freshwatch.merchandiser.add.merchandiser_name_placeholder'
                    )}
                    type="text"
                    variant="outlined"
                    autoComplete="off"
                    value={formik.values.merchandiserName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                    error={
                      formik.touched.merchandiserName &&
                      Boolean(formik.errors.merchandiserName)
                    }
                    helperText={
                      formik.touched.merchandiserName &&
                      formik.errors.merchandiserName && (
                        <ErrorText>
                          {polyglot.t(formik.errors.merchandiserName)}
                        </ErrorText>
                      )
                    }
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  sx={{
                    gap: '0.5rem',
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <FormLabel
                    htmlFor="merchandiserType"
                    required
                    sx={{ color: 'text.primary' }}
                  >
                    {polyglot.t(
                      'freshwatch.merchandiser.add.merchandiser_type'
                    )}
                  </FormLabel>

                  <Select
                    id="merchandiserType"
                    value={formik.values.merchandiserType}
                    onChange={(value) => {
                      formik.setFieldValue(
                        'merchandiserType',
                        value.target.value
                      );
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.merchandiserType &&
                      Boolean(formik.errors.merchandiserType)
                    }
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return (
                          <Typography
                            variant="h5"
                            sx={{ opacity: 0.6 }}
                            color="text.secondary"
                          >
                            {polyglot.t(
                              'freshwatch.merchandiser.add.merchandiser_type_placeholder'
                            )}
                          </Typography>
                        );
                      }
                      return (
                        machineTypes.find((type) => type.id === selected)
                          ?.name || ''
                      );
                    }}
                  >
                    {machineTypes.map((machineType) => (
                      <MenuItem value={machineType.id} key={machineType.id}>
                        {machineType.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {machineTypes.length === 0 && (
                    <ErrorText>
                      {polyglot.t(
                        'freshwatch.merchandiser.add.merchandiser_type_required'
                      )}
                    </ErrorText>
                  )}
                  {formik.touched.merchandiserType &&
                    formik.errors.merchandiserType && (
                      <ErrorText>
                        <Typography variant="body2" color="error">
                          {polyglot.t(formik.errors.merchandiserType)}
                        </Typography>
                      </ErrorText>
                    )}
                </FormControl>
                <FormControl
                  fullWidth
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '0.5rem',
                  }}
                >
                  <FormLabel
                    htmlFor="commanderSerialNumber"
                    required
                    sx={{ color: 'text.primary' }}
                  >
                    {polyglot.t('freshwatch.merchandiser.add.freshwatch_code')}
                  </FormLabel>

                  <TextField
                    fullWidth
                    id="commanderSerialNumber"
                    name="commanderSerialNumber"
                    placeholder={polyglot.t(
                      'freshwatch.merchandiser.add.freshwatch_code_placeholder'
                    )}
                    type="text"
                    variant="outlined"
                    autoComplete="off"
                    value={formik.values.commanderSerialNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                    error={
                      formik.touched.commanderSerialNumber &&
                      Boolean(formik.errors.commanderSerialNumber)
                    }
                    helperText={
                      formik.errors.commanderSerialNumber && (
                        <ErrorText>
                          {polyglot.t(formik.errors.commanderSerialNumber)}
                        </ErrorText>
                      )
                    }
                  />
                </FormControl>
                <AddressFormFields formik={formik} />{' '}
                {/* Optional address fields for the merchandiser */}
              </Stack>
              <Box
                display="flex"
                gap="1rem"
                justifyContent="space-around"
                width="100%"
                paddingTop={2}
              >
                <Button onClick={onClose} variant="outlined" fullWidth>
                  {polyglot.t('button.cancel')}
                </Button>
                <Button
                  type="submit"
                  onClick={() => formik.handleSubmit()}
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                  fullWidth
                  autoFocus={true}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    polyglot.t('button.ok')
                  )}
                </Button>
              </Box>
            </form>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Stack>
      )}
    </DialogModal>
  );
};

export default AddMerchandiserModal;
